/** Small geometry helpers shared by the scene builder. */
import * as THREE from 'three';
import type { Vec2 } from '../../core/hp/types';
import { withoutHoles } from '../../core/geometry/stairwells';

export type P3 = [number, number, number];

/**
 * Extrude the same native joint polygon drawn by the plan; groups stay left/right/edge. `y1` may be a height that varies
 * linearly along the wall (a wall a sloped ceiling comes down on, `wallTop`): then the top is a plane through it.
 */
export function polygonPrismGeometry(
  polygon: readonly Vec2[], y0: number, y1: number | ((p: Vec2) => number), wallStart: Vec2, wallEnd: Vec2,
): THREE.BufferGeometry {
  const top = typeof y1 === 'number' ? () => y1 : y1;
  const points = polygon.map(p => ({ ...p }));
  const area = points.reduce((sum,p,i) => { const q=points[(i+1)%points.length];return sum+p.x*q.z-q.x*p.z; },0);
  if(area<0)points.reverse();
  const length=Math.hypot(wallEnd.x-wallStart.x,wallEnd.z-wallStart.z)||1;
  const d={x:(wallEnd.x-wallStart.x)/length,z:(wallEnd.z-wallStart.z)/length};
  const n={x:-d.z,z:d.x};
  const along=(p:Vec2)=>(p.x-wallStart.x)*d.x+(p.z-wallStart.z)*d.z;
  const across=(p:Vec2)=>(p.x-wallStart.x)*n.x+(p.z-wallStart.z)*n.z;
  const positions:number[]=[],uv:number[]=[],groups:number[][]=[[],[],[]];
  const face=(vertices:P3[],coords:number[],indices:number[],group:number)=>{
    const offset=positions.length/3;
    vertices.forEach(p=>positions.push(...p));uv.push(...coords);
    groups[group].push(...indices.map(i=>i+offset));
  };
  for(let i=0;i<points.length;i++) {
    const a=points[i],b=points[(i+1)%points.length],ya=top(a),yb=top(b);
    const parallel=Math.abs((b.x-a.x)*n.x+(b.z-a.z)*n.z)<1e-7;
    const group=parallel ? (across(a)<0?0:1) : 2;
    const u0=group===2?0:along(a),u1=group===2?Math.hypot(b.x-a.x,b.z-a.z):along(b);
    face([[a.x,y0,a.z],[a.x,ya,a.z],[b.x,yb,b.z],[b.x,y0,b.z]],
      [u0,y0,u0,ya,u1,yb,u1,y0],[0,1,2,0,2,3],group);
  }
  const triangles=THREE.ShapeUtils.triangulateShape(points.map(p=>new THREE.Vector2(p.x,p.z)),[]);
  const capUV=points.flatMap(p=>[p.x,p.z]);
  face(points.map(p=>[p.x,y0,p.z]),capUV,triangles.flat(),2);
  face(points.map(p=>[p.x,top(p),p.z]),capUV,triangles.flatMap(([a,b,c])=>[c,b,a]),2);
  const g=new THREE.BufferGeometry();
  g.setAttribute('position',new THREE.Float32BufferAttribute(positions,3));
  g.setAttribute('uv',new THREE.Float32BufferAttribute(uv,2));
  g.setIndex(groups.flat());
  let start=0;groups.forEach((indices,group)=>{g.addGroup(start,indices.length,group);start+=indices.length;});
  g.computeVertexNormals();
  return g;
}

/**
 * A quad from four world-space points, wound p0→p1→p2→p3.
 * `uvs` are given in metres so tiled materials line up across separate meshes.
 */
export function quadGeometry(
  p0: P3, p1: P3, p2: P3, p3: P3,
  uvs: [number, number, number, number, number, number, number, number],
): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute([...p0, ...p1, ...p2, ...p3], 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex([0, 1, 2, 0, 2, 3]);
  g.computeVertexNormals();
  return g;
}

/**
 * A prism: the plan quad [leftStart, leftEnd, rightEnd, rightStart] extruded from y0 to y1.
 *
 * Material groups, in order: 0 = left face, 1 = right face, 2 = everything else
 * (top, bottom and the two ends). UVs on the big faces run in metres — u along the
 * wall (continuing the parent wall's own u), v upward — so a texture is continuous
 * across panels that an opening split apart.
 */
export function prismGeometry(
  quad: readonly [Vec2, Vec2, Vec2, Vec2],
  y0: number, y1: number, u0: number, u1: number,
): THREE.BufferGeometry {
  const [ls, le, re, rs] = quad;
  const v: P3[] = [
    [ls.x, y0, ls.z], [le.x, y0, le.z], [re.x, y0, re.z], [rs.x, y0, rs.z],
    [ls.x, y1, ls.z], [le.x, y1, le.z], [re.x, y1, re.z], [rs.x, y1, rs.z],
  ];
  const t = Math.hypot(ls.x - rs.x, ls.z - rs.z);   // wall thickness at this span

  const pos: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  const groups: Array<[number, number]> = [];
  const quadFace = (a: number, b: number, c: number, d: number, u: number[]) => {
    const base = pos.length / 3;
    for (const i of [a, b, c, d]) pos.push(...v[i]);
    uv.push(...u);
    const start = idx.length;
    idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    groups.push([start, 6]);
  };

  // left face (the ls→le side), outward normal = −perp(dir)
  quadFace(0, 4, 5, 1, [u0, y0, u0, y1, u1, y1, u1, y0]);
  // right face (rs→re side)
  quadFace(3, 2, 6, 7, [u0, y0, u1, y0, u1, y1, u0, y1]);
  // top
  quadFace(4, 7, 6, 5, [0, u0, t, u0, t, u1, 0, u1]);
  // bottom
  quadFace(0, 1, 2, 3, [u0, 0, u1, 0, u1, t, u0, t]);
  // end at u0 (the ls→rs edge)
  quadFace(0, 3, 7, 4, [0, y0, t, y0, t, y1, 0, y1]);
  // end at u1 (the le→re edge)
  quadFace(1, 5, 6, 2, [0, y0, 0, y1, t, y1, t, y0]);

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  g.clearGroups();
  g.addGroup(groups[0][0], groups[0][1], 0);           // left
  g.addGroup(groups[1][0], groups[1][1], 1);           // right
  g.addGroup(groups[2][0], idx.length - groups[2][0], 2); // the rest
  g.computeVertexNormals();
  return g;
}

/**
 * Room outline → a horizontal slab at height `y`. UVs come out in metres.
 *
 * `ShapeGeometry` builds in the XY plane and `rotateX(-90°)` maps shape-Y to world −Z,
 * so the outline is fed in pre-negated; getting this wrong mirrors the whole floor
 * against the walls, which is exactly the kind of error a screenshot hides.
 */
export function slabGeometry(poly: Vec2[], y: number, faceDown: boolean, holes: readonly (readonly Vec2[])[] = []): THREE.BufferGeometry {
  // A stairwell leaves the slab in pieces; their UVs still come from the same world coordinates, so no seam shows.
  const pieces = withoutHoles(poly, holes);
  const shapes = pieces.map(piece => new THREE.Shape(piece.map((p) => new THREE.Vector2(p.x, -p.z))));
  const g = new THREE.ShapeGeometry(shapes.length === 1 ? shapes[0] : shapes);
  g.rotateX(-Math.PI / 2);                    // positions (x, 0, z), normal +Y
  if (faceDown) {
    const idx = g.getIndex();
    if (idx) {
      const a = idx.array as unknown as number[];
      for (let i = 0; i < a.length; i += 3) { const t = a[i]; a[i] = a[i + 2]; a[i + 2] = t; }
      idx.needsUpdate = true;
    }
    g.computeVertexNormals();                 // winding reversed → normal −Y
  }
  g.translate(0, y, 0);
  return g;
}

/**
 * The edge of a hole in a slab, as the app gives a floor or ceiling opening its slopes (HorizontalOpeningController,
 * SlopeDepth): a strip round the counter-clockwise `hole` from `y0` to `y1`, facing into the hole, UVs in metres along
 * the edge and up it.
 */
export function holeEdgeGeometry(hole: readonly Vec2[], y0: number, y1: number): THREE.BufferGeometry {
  const positions: number[] = [], uv: number[] = [], index: number[] = [];
  let u = 0;
  for (let i = 0; i < hole.length; i++) {
    const a = hole[i], b = hole[(i + 1) % hole.length], length = Math.hypot(b.x - a.x, b.z - a.z);
    if (length < 1e-6) continue;
    const base = positions.length / 3;
    positions.push(a.x, y0, a.z, b.x, y0, b.z, b.x, y1, b.z, a.x, y1, a.z);
    uv.push(u, y0, u + length, y0, u + length, y1, u, y1);
    // Wound so the face looks into the hole whichever of y0 and y1 is higher.
    index.push(...(y1 > y0 ? [base, base + 1, base + 2, base, base + 2, base + 3] : [base, base + 2, base + 1, base, base + 3, base + 2]));
    u += length;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(index);
  g.computeVertexNormals();
  return g;
}
